import React, { useState, useEffect } from "react";
import TestCreator from "../pages/TestCreator";
import TestEditor from "../pages/TestEditor";
import { useAuth } from "../../AuthContext";
import { url } from "../../Config";
import { MdDeleteOutline } from "react-icons/md";

const Sidebar = () => {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("my-tests");
  const [myTests, setMyTests] = useState([]);
  const [editingTest, setEditingTest] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = userType === "premium" || userType === "admin";

  useEffect(() => {
    if (!user?.token) return;

    fetch(`${url}/api/me`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((res) => res.json())
      .then((data) => setUserType(data.user_type))
      .catch((err) => console.error(err));
  }, [user]);

  useEffect(() => {
    if (activeTab === "my-tests" && user?.token) {
      setLoading(true);

      fetch(`${url}/api/my-tests`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
        .then((res) => res.json())
        .then((data) => setMyTests(Array.isArray(data) ? data : []))
        .catch((err) => {
          console.error(err);
          setMyTests([]);
        })
        .finally(() => setLoading(false));
    }
  }, [activeTab, user]);

  return (
    <div className="flex min-h-screen">

      {/* PC SIDEBAR */}
      <div className="hidden md:block w-64 bg-gray-200 p-4">
        <h2 className="text-xl font-bold mb-4">Dashboard</h2>

        <button
          className={`block w-full text-left p-2 mb-2 rounded ${
            activeTab === "my-tests" ? "bg-blue-500 text-white" : ""
          }`}
          onClick={() => {
            setEditingTest(null);
            setActiveTab("my-tests");
          }}
        >
          My Tests
        </button>

        <button
          className={`block w-full text-left p-2 mb-2 rounded ${
            activeTab === "create-test" ? "bg-blue-500 text-white" : ""
          } ${!isPremium ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={() => {
            if (!isPremium) {
              setActiveTab("access-denied");
              return;
            }
            setEditingTest(null);
            setActiveTab("create-test");
          }}
        >
          Create Test
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 bg-white">

        {/* MOBILE HEADER PANEL */}
        <div className="md:hidden mb-4 -mx-0 px-10 overflow-x-auto space-x-2 bg-gray-100 rounded py-3 min-h-[60px] flex items-center">
          <button
            className={`flex-shrink-0 px-4 py-2 rounded ${
              activeTab === "my-tests" ? "bg-blue-500 text-white" : ""
            }`}
            onClick={() => {
              setEditingTest(null);
              setActiveTab("my-tests");
            }}
          >
            My Tests
          </button>

          <button
            className={`flex-shrink-0 px-4 py-2 rounded ${
              activeTab === "create-test" ? "bg-blue-500 text-white" : ""
            } ${!isPremium ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => {
              if (!isPremium) {
                setActiveTab("access-denied");
                return;
              }
              setEditingTest(null);
              setActiveTab("create-test");
            }}
          >
            Create Test
          </button>
        </div>

        {/* =========================
            MY TESTS TAB
        ========================= */}
        {activeTab === "my-tests" && !editingTest && (
          <div>
            <h2 className="text-2xl font-bold mb-4 pl-5 pt-5 md:pl-10 md:pt-10">
              My Tests
            </h2>

            {loading ? (
              <div className="text-center py-16 text-blue-700 text-lg">
                Loading your tests...
              </div>
            ) : myTests.length === 0 ? (
              <div className="text-center py-16">
                <h3 className="text-xl font-semibold mb-2">
                  You haven’t created any tests yet
                </h3>
                <p className="text-gray-600 mb-4">
                  Start creating psychological tests and share them with others.
                </p>

                {isPremium ? (
                  <button
                    onClick={() => setActiveTab("create-test")}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Create Your First Test
                  </button>
                ) : (
                  <p className="text-gray-500">
                    Upgrade to{" "}
                    <span className="font-semibold text-yellow-600">
                      Premium
                    </span>{" "}
                    to start creating tests.
                  </p>
                )}
              </div>
            ) : (
              <ul className="space-y-2 px-5 md:px-10">
                {myTests.map((test) => (
                  <li
                    key={test._id}
                    className="p-3 border rounded flex justify-between items-center"
                  >
                    <span>{test.testName}</span>

                    <div className="flex space-x-2">

                      {/* DELETE */}
                      <button
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Are you sure you want to delete "${test.testName}"?`
                            )
                          )
                            return;

                          try {
                            const res = await fetch(
                              `${url}/api/test/${test.slug}`,
                              {
                                method: "DELETE",
                                headers: {
                                  Authorization: `Bearer ${user.token}`,
                                },
                              }
                            );

                            const data = await res.json();

                            if (res.ok) {
                              setMyTests((prev) =>
                                prev.filter((t) => t._id !== test._id)
                              );
                              alert(data.message);
                            } else {
                              alert(data.message || "Failed to delete test");
                            }
                          } catch (err) {
                            console.error(err);
                            alert("Error deleting test");
                          }
                        }}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                      >
                        <MdDeleteOutline />
                      </button>

                      {/* EDIT */}
                      <button
                        onClick={() => setEditingTest(test)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition"
                      >
                        Edit
                      </button>

                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* =========================
            ACCESS DENIED
        ========================= */}
        {activeTab === "access-denied" && (
          <div className="text-center mt-20">
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-gray-600">
              Creating tests is only available for Premium users.
            </p>
          </div>
        )}

        {/* =========================
            TEST EDITOR
        ========================= */}
        {editingTest && (
          <TestEditor
            initialData={editingTest}
            slug={editingTest.slug || editingTest._id}
            user={user}
            navigate={() => {
              setEditingTest(null);
              setActiveTab("my-tests");
            }}
          />
        )}

        {/* =========================
            CREATE TEST
        ========================= */}
        {activeTab === "create-test" && !editingTest && <TestCreator />}
      </div>
    </div>
  );
};

export default Sidebar;